import { Play, PenLine, Hammer, Target, type LucideIcon } from "lucide-react";

interface WrapStep {
  title: string;
  body: string;
}

const meta: { icon: LucideIcon; tag: string }[] = [
  { icon: Play, tag: "30 minutes, live" },
  { icon: PenLine, tag: "1-3 questions" },
  { icon: Hammer, tag: "On the job" },
  { icon: Target, tag: "Tracked results" },
];

export function WrapMethodology({ steps }: { steps: readonly WrapStep[] }) {
  return (
    <div className="relative">
      {/* connecting rail: vertical on mobile, horizontal on desktop */}
      <span
        aria-hidden
        className="absolute left-[27px] top-6 bottom-6 w-px bg-gradient-to-b from-primary/40 via-primary/20 to-transparent md:left-0 md:right-0 md:top-[27px] md:bottom-auto md:h-px md:w-auto md:bg-gradient-to-r md:from-primary/40 md:via-primary/25 md:to-transparent"
      />

      <ol className="relative grid gap-4 md:grid-cols-4 md:gap-6">
        {steps.map((step, i) => {
          const { icon: Icon, tag } = meta[i] ?? meta[0];
          const letter = step.title.charAt(0);
          return (
            <li
              key={step.title}
              className="group relative grid grid-cols-[auto_minmax(0,1fr)] items-start gap-4 rounded-2xl border border-border/60 bg-background p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-brand md:block md:p-6"
            >
              {/* letter medallion */}
              <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary-glow text-2xl font-semibold text-primary-foreground shadow-brand">
                <span style={{ fontFamily: "'Poppins', sans-serif" }}>{letter}</span>
              </span>

              <div className="min-w-0 md:mt-5">
                <div className="flex items-center gap-2">
                  <h3
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                    className="text-lg font-semibold text-ink"
                  >
                    {step.title}
                  </h3>
                  <Icon className="h-4 w-4 shrink-0 text-primary" strokeWidth={1.75} />
                </div>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-primary">
                  {tag}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
              </div>

              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] origin-left scale-x-0 rounded-b-2xl bg-gradient-to-r from-primary to-primary-glow transition-transform duration-500 group-hover:scale-x-100"
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}
